import React from 'react';

/**
 * Значки плеера.
 *
 * Свои, а не из общего набора оболочки: у плеера своя толщина линии и свой
 * масштаб — кнопки здесь крупнее, а смотрят на них с дивана, — и правка
 * значков навигации не должна менять панель плеера. Всё вектором и тем же
 * `currentColor`: цвет задаёт кнопка, а не значок.
 */

type IconProps = { className?: string };

function stroke(children: React.ReactNode, strokeWidth = 1.8): React.ComponentType<IconProps> {
  return function PlayerStrokeIcon({ className }: IconProps): React.JSX.Element {
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {children}
      </svg>
    );
  };
}

export const BackIcon = stroke(<path d="M14.5 5.5 8 12l6.5 6.5" />, 2.2);

export const SubtitlesIcon = stroke(
  <>
    <rect x="3" y="5" width="18" height="14" rx="3.5" />
    <path d="M7 11.5h3M12.5 11.5H17M7 15h6.5" />
  </>,
);

export const QualityIcon = stroke(
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.4 13.4a7.7 7.7 0 0 0 0-2.8l2-1.5-2-3.4-2.3.7a7.7 7.7 0 0 0-2.5-1.4L14 2.5h-4l-.5 2.5a7.7 7.7 0 0 0-2.5 1.4l-2.3-.7-2 3.4 2 1.5a7.7 7.7 0 0 0 0 2.8l-2 1.5 2 3.4 2.3-.7a7.7 7.7 0 0 0 2.5 1.4l.5 2.5h4l.5-2.5a7.7 7.7 0 0 0 2.5-1.4l2.3.7 2-3.4-2-1.5Z" />
  </>,
);

export const AudioIcon = stroke(
  <>
    <path d="M4 9.5h3l4.5-4v13L7 14.5H4Z" />
    <path d="M15.5 9.2a4 4 0 0 1 0 5.6M18.2 6.6a7.6 7.6 0 0 1 0 10.8" />
  </>,
);

export const MutedIcon = stroke(
  <>
    <path d="M4 9.5h3l4.5-4v13L7 14.5H4Z" />
    <path d="m16 9.5 5 5M21 9.5l-5 5" />
  </>,
);

export function MoreIcon({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="5" width="18" height="14" rx="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="16" cy="12" r="1.3" fill="currentColor" />
    </svg>
  );
}

export const FullscreenIcon = stroke(
  <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15" />,
);

export const ExitFullscreenIcon = stroke(
  <path d="M9 4v3.5A1.5 1.5 0 0 1 7.5 9H4M15 4v3.5A1.5 1.5 0 0 0 16.5 9H20M20 15h-3.5a1.5 1.5 0 0 0-1.5 1.5V20M4 15h3.5A1.5 1.5 0 0 1 9 16.5V20" />,
);

export const ChevronDownIcon = stroke(<path d="m7 10 5 5 5-5" />, 2);

/** Пуск: треугольник со скруглёнными углами — заливкой и обводкой одного цвета. */
export function PlayGlyph({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M8 5.6v12.8a.9.9 0 0 0 1.37.77l10.2-6.4a.9.9 0 0 0 0-1.54L9.37 4.83A.9.9 0 0 0 8 5.6Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function PauseGlyph({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="6.2" y="5" width="4" height="14" rx="1.3" fill="currentColor" />
      <rect x="13.8" y="5" width="4" height="14" rx="1.3" fill="currentColor" />
    </svg>
  );
}

/** Предыдущая серия: черта и треугольник к ней. */
export function SkipPreviousIcon({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="5" y="5.5" width="2.4" height="13" rx="1.2" fill="currentColor" />
      <path d="M18.5 6.2v11.6a.8.8 0 0 1-1.24.66l-8.6-5.8a.8.8 0 0 1 0-1.32l8.6-5.8a.8.8 0 0 1 1.24.66Z" fill="currentColor" />
    </svg>
  );
}

/** Следующая серия: треугольник, упёршийся в черту. */
export function SkipNextIcon({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5.5 6.2v11.6a.8.8 0 0 0 1.24.66l8.6-5.8a.8.8 0 0 0 0-1.32l-8.6-5.8a.8.8 0 0 0-1.24.66Z" fill="currentColor" />
      <rect x="16.6" y="5.5" width="2.4" height="13" rx="1.2" fill="currentColor" />
    </svg>
  );
}
